import React, { useMemo, useState } from 'react';
import { Badge, Card, Dropdown, DropdownButton, OverlayTrigger, Tooltip } from 'react-bootstrap';
import { fetchTicketsByPlates, updatePlateInDataBase } from '../Functions';
import { useAppDispatch, useAppSelector } from '../hooks';
import { removePlateFromClient, updatePlateFromClient } from '../ReduxSlices/clientsSlice';
import { setEditingPlate } from '../ReduxSlices/platesSlice';
import { setChangeLogs } from '../ReduxSlices/changeLogsSlice';
import { PlateClassification, MonitoringRange, Plate } from '../Types';
import { useMonitoringListQuery } from '../queries/monitoring.query';
import {
  FaCalendarAlt,
  FaMapMarkerAlt,
  FaEye,
  FaEyeSlash,
  FaArchive,
  FaEdit,
  FaHistory,
  FaTicketAlt,
  FaDownload,
  FaExclamationTriangle,
  FaFileAlt,
  FaCheckCircle,
  FaTimesCircle,
  FaExchangeAlt
} from 'react-icons/fa';
import PlateRegistrationModal from './Registration/PlateRegistrationModal';
import PlateTransferModal from './PlateTransferModal';
import { PlateRegistrationSummary } from '../types/plateRegistration.types';

const getClassificationBadge = (classification: PlateClassification) => {
  switch (classification) {
    case PlateClassification.Business:
      return { label: 'B', fullLabel: 'Business', bg: 'success' };
    case PlateClassification.Personal:
      return { label: 'P', fullLabel: 'Personal', bg: 'info' };
    default:
      return { label: 'U', fullLabel: 'Unassigned', bg: 'secondary' };
  }
};

const formatDate = (dateString: string | undefined) => {
  if (!dateString) return null;
  const date = new Date(dateString);
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
};

const isExpiringSoon = (dateString: string | undefined) => {
  if (!dateString) return false;
  const expDate = new Date(dateString);
  const today = new Date();
  const daysUntilExpiry = Math.ceil((expDate.getTime() - today.getTime()) / (1000 * 60 * 60 * 24));
  return daysUntilExpiry <= 30 && daysUntilExpiry > 0;
};

const isExpired = (dateString: string | undefined) => {
  if (!dateString) return false;
  const expDate = new Date(dateString);
  const today = new Date();
  return expDate < today;
};

function PlateCard({
  plate,
  clientId,
  registration
}: {
  plate: Plate & {
    monitoringRange: MonitoringRange;
    registrationExpirationDate?: string;
    registrationAddress?: string;
  };
  clientId?: string;
  registration?: PlateRegistrationSummary;
}) {
  const [showRegistrationModal, setShowRegistrationModal] = useState(false);
  const [showTransferModal, setShowTransferModal] = useState(false);
  const dispatch = useAppDispatch();
  const editingPlate = useAppSelector((state) => state.plates.editingPlate);
  const { data } = useMonitoringListQuery({ enabled: true });
  const { unassignedMonitoringItem } = data ?? {};
  const monitoringListId = useAppSelector(
    (state) =>
      state.clients.allClientsList.find((client) => client.id === state.clients.selectedClientId)!
        .monitoringListId
  );
  const clientIsMonitored = unassignedMonitoringItem?.id !== monitoringListId;

  // Automated monitoring skips Business (corporate-registered) plates.
  const isBusinessPlate = plate.classification === PlateClassification.Business;

  const plateIsCurrentlyMonitored = useMemo(
    function () {
      if (!clientIsMonitored || isBusinessPlate) {
        return false;
      }
      const currentDate = new Date();
      const rangeIsActive =
        currentDate > new Date(plate?.monitoringRange?.startDate) &&
        currentDate < new Date(plate?.monitoringRange?.endDate);
      return rangeIsActive;
    },
    [clientIsMonitored, isBusinessPlate, plate?.monitoringRange]
  );

  const toggleArchived = () =>
    updatePlateInDataBase(
      {
        ...plate,
        archivedAt: plate.archivedAt ? null : new Date().toISOString()
      },
      (modifiedPlate: Plate) => dispatch(updatePlateFromClient(modifiedPlate))
    );

  const showPlateChangeLogsModal = (id: string) => {
    dispatch(
      setChangeLogs({
        showChangeLogs: true,
        entityId: id,
        entityName: 'Plate'
      })
    );
  };

  const handlePlateTransferred = () => {
    // The plate now belongs to another client, so it leaves this client's list.
    if (editingPlate && editingPlate.id === plate.id) dispatch(setEditingPlate(null));
    dispatch(removePlateFromClient(plate));
  };

  const isBeingEdited = editingPlate && plate.id === editingPlate.id;
  const expired = isExpired(plate.registrationExpirationDate);
  const expiringSoon = isExpiringSoon(plate.registrationExpirationDate);

  // Determine card border color based on status
  const getBorderColor = () => {
    if (isBeingEdited) return '#ffc107';
    if (plate.archivedAt) return '#6c757d';
    if (expired) return '#dc3545';
    if (expiringSoon) return '#fd7e14';
    if (plate.tickets) return '#dc3545';
    return '#1a365d';
  };

  return (
    <Card
      className="shadow-sm"
      style={{
        width: '280px',
        minWidth: '280px',
        border: `2px solid ${getBorderColor()}`,
        borderRadius: '12px',
        opacity: plate.archivedAt ? 0.7 : 1
      }}>
      {/* License Plate Visual */}
      <div
        style={{
          background: plate.archivedAt
            ? 'linear-gradient(180deg, #9ca3af 0%, #6b7280 100%)'
            : 'linear-gradient(180deg, #FFD700 0%, #FFC107 100%)',
          borderRadius: '10px 10px 0 0',
          padding: '12px 16px',
          position: 'relative'
        }}>
        {/* State Label */}
        <div
          style={{
            fontSize: '9px',
            fontWeight: 'bold',
            color: '#1a365d',
            letterSpacing: '2px',
            textAlign: 'center',
            marginBottom: '2px'
          }}>
          {(typeof plate.state === 'string' ? plate.state : plate.state?.label)?.toUpperCase() ||
            'NEW YORK'}
        </div>

        {/* Plate Number */}
        <div
          style={{
            fontFamily: 'FE-Schrift, monospace',
            fontSize: '28px',
            fontWeight: 'bold',
            color: '#1a365d',
            letterSpacing: '3px',
            textAlign: 'center',
            textDecoration: plate.archivedAt ? 'line-through' : 'none'
          }}>
          {plate.plateNumber.toUpperCase()}
        </div>

        {/* Status Icons Row */}
        <div
          className="d-flex justify-content-center gap-2 mt-2"
          style={{ fontSize: '11px', color: '#1a365d' }}>
          {plateIsCurrentlyMonitored ? (
            <OverlayTrigger
              placement="top"
              overlay={<Tooltip id="monitored-tooltip">Actively Monitored</Tooltip>}>
              <span className="d-flex align-items-center gap-1">
                <FaEye color="#059669" /> Monitored
              </span>
            </OverlayTrigger>
          ) : (
            <OverlayTrigger
              placement="top"
              overlay={
                <Tooltip id="not-monitored-tooltip">
                  {isBusinessPlate
                    ? 'Not monitored: registered to a corporation'
                    : 'Not Being Monitored'}
                </Tooltip>
              }>
              <span className="d-flex align-items-center gap-1" style={{ opacity: 0.6 }}>
                <FaEyeSlash /> Not Monitored
              </span>
            </OverlayTrigger>
          )}
          {plate.archivedAt && (
            <OverlayTrigger
              placement="top"
              overlay={<Tooltip id="archived-tooltip">This plate is archived</Tooltip>}>
              <span className="d-flex align-items-center gap-1">
                <FaArchive color="#6c757d" /> Archived
              </span>
            </OverlayTrigger>
          )}
          <OverlayTrigger
            placement="top"
            overlay={
              <Tooltip id="classification-tooltip">
                Classification: {getClassificationBadge(plate.classification).fullLabel}
                {plate.classificationReviewedAt && (
                  <div>
                    Verified from registration
                    {plate.classificationReviewedByName
                      ? ` by ${plate.classificationReviewedByName}`
                      : ''}{' '}
                    on {formatDate(plate.classificationReviewedAt)}
                  </div>
                )}
                {plate.businessNoticeSentAt && (
                  <div>Client notified {formatDate(plate.businessNoticeSentAt)}</div>
                )}
              </Tooltip>
            }>
            <span>
              <Badge bg={getClassificationBadge(plate.classification).bg} className="ms-1">
                {getClassificationBadge(plate.classification).label}
                {plate.classificationReviewedAt && <FaCheckCircle size={9} className="ms-1" />}
              </Badge>
            </span>
          </OverlayTrigger>
        </div>

        {/* Dropdown Menu */}
        <DropdownButton
          variant="link"
          size="sm"
          id={plate.id + 'DropdownButton'}
          title=""
          style={{
            position: 'absolute',
            top: '8px',
            right: '8px'
          }}
          className="plate-dropdown">
          {!plate.archivedAt && (
            <Dropdown.Item
              onClick={() =>
                dispatch(
                  setEditingPlate(editingPlate && plate.id === editingPlate.id ? null : plate)
                )
              }
              className="d-flex align-items-center gap-2">
              <FaEdit /> {editingPlate && plate.id === editingPlate.id ? 'Cancel Edit' : 'Edit'}
            </Dropdown.Item>
          )}
          <Dropdown.Item onClick={toggleArchived} className="d-flex align-items-center gap-2">
            <FaArchive /> {plate.archivedAt ? 'Unarchive' : 'Archive'}
          </Dropdown.Item>
          <Dropdown.Item
            onClick={() => setShowTransferModal(true)}
            className="d-flex align-items-center gap-2">
            <FaExchangeAlt /> Transfer to Another Client
          </Dropdown.Item>
          <Dropdown.Divider />
          <Dropdown.Item
            onClick={() =>
              fetchTicketsByPlates([plate.plateNumber], {
                clientId: plate.clientId
              })
            }
            className="d-flex align-items-center gap-2">
            <FaTicketAlt /> Get Tickets
          </Dropdown.Item>
          <Dropdown.Item
            onClick={() =>
              fetchTicketsByPlates(
                [plate.plateNumber],
                {
                  clientId: plate.clientId
                },
                true
              )
            }
            className="d-flex align-items-center gap-2">
            <FaDownload /> Import Tickets (Admin)
          </Dropdown.Item>
          <Dropdown.Divider />
          <Dropdown.Item
            onClick={() => showPlateChangeLogsModal(plate.id)}
            className="d-flex align-items-center gap-2">
            <FaHistory /> History
          </Dropdown.Item>
          <Dropdown.Item
            onClick={() => setShowRegistrationModal(true)}
            className="d-flex align-items-center gap-2">
            <FaFileAlt /> Registration File
          </Dropdown.Item>
        </DropdownButton>
      </div>

      {/* Registration Info Section */}
      <Card.Body className="py-2 px-3" style={{ backgroundColor: '#f8f9fa' }}>
        {/* Registration Expiration */}
        <div className="d-flex align-items-center gap-2 mb-2">
          <FaCalendarAlt
            size={14}
            color={expired ? '#dc3545' : expiringSoon ? '#fd7e14' : '#6c757d'}
          />
          <span style={{ fontSize: '12px', color: '#495057' }}>
            <strong>Reg. Expires:</strong>{' '}
            {plate.registrationExpirationDate ? (
              <span
                style={{
                  color: expired ? '#dc3545' : expiringSoon ? '#fd7e14' : '#212529',
                  fontWeight: expired || expiringSoon ? 'bold' : 'normal'
                }}>
                {formatDate(plate.registrationExpirationDate)}
                {expired && (
                  <OverlayTrigger
                    placement="top"
                    overlay={<Tooltip id="expired-tooltip">Registration has expired!</Tooltip>}>
                    <span className="ms-1">
                      <FaExclamationTriangle color="#dc3545" size={12} />
                    </span>
                  </OverlayTrigger>
                )}
                {expiringSoon && !expired && (
                  <OverlayTrigger
                    placement="top"
                    overlay={
                      <Tooltip id="expiring-tooltip">Registration expiring within 30 days</Tooltip>
                    }>
                    <span className="ms-1">
                      <FaExclamationTriangle color="#fd7e14" size={12} />
                    </span>
                  </OverlayTrigger>
                )}
              </span>
            ) : (
              <span className="text-muted fst-italic">Not set</span>
            )}
          </span>
        </div>

        {/* Registration File */}
        <div className="d-flex align-items-center gap-2 mb-2">
          {registration?.hasCurrentRegistration ? (
            <FaCheckCircle size={14} color="#198754" />
          ) : (
            <FaTimesCircle size={14} color="#dc3545" />
          )}
          <span style={{ fontSize: '12px', color: '#495057' }}>
            <strong>Registration:</strong>{' '}
            {registration?.hasCurrentRegistration ? (
              <Badge
                bg="success"
                style={{ cursor: 'pointer' }}
                onClick={() => setShowRegistrationModal(true)}>
                On file
              </Badge>
            ) : (
              <Badge
                bg="danger"
                style={{ cursor: 'pointer' }}
                onClick={() => setShowRegistrationModal(true)}>
                Missing
              </Badge>
            )}
          </span>
        </div>

        {/* Registration Address */}
        <div className="d-flex align-items-start gap-2">
          <FaMapMarkerAlt size={14} color="#6c757d" style={{ marginTop: '2px', flexShrink: 0 }} />
          <span style={{ fontSize: '12px', color: '#495057' }}>
            <strong>Address:</strong>{' '}
            {plate.registrationAddress ? (
              <span style={{ wordBreak: 'break-word' }}>{plate.registrationAddress}</span>
            ) : (
              <span className="text-muted fst-italic">Not set</span>
            )}
          </span>
        </div>
      </Card.Body>

      {showRegistrationModal && (
        <PlateRegistrationModal
          show={showRegistrationModal}
          onHide={() => setShowRegistrationModal(false)}
          plateId={plate.id}
          plateNumber={plate.plateNumber}
          clientId={clientId}
        />
      )}

      {showTransferModal && (
        <PlateTransferModal
          show={showTransferModal}
          plate={plate}
          onHide={() => setShowTransferModal(false)}
          onTransferred={handlePlateTransferred}
        />
      )}
    </Card>
  );
}

export default PlateCard;
